/**
 * Certification Verification Agent
 * Verifies and tracks farm certifications (organic, local, sustainable, etc.)
 */

interface Certification {
  id: string;
  name: string;
  shortName: string;
  type: 'organic' | 'sustainability' | 'safety' | 'local' | 'specialty';
  issuingBody: string;
  description: string;
  requirements: string[];
  validityPeriod: number; // months
  verificationUrl?: string;
}

interface FarmCertification {
  certificationId: string;
  farmId: string;
  issueDate: Date;
  expiryDate: Date;
  certificateNumber: string;
  status: 'active' | 'pending' | 'expired' | 'suspended';
  verifiedDate?: Date;
  notes?: string;
}

interface CertificationSummary {
  farmId: string;
  activeCertifications: Certification[];
  pendingCertifications: Certification[];
  expiringSoon: Certification[];
  trustScore: number;
  badges: string[];
}

class CertificationVerificationAgent {
  private certifications: Certification[] = [
    {
      id: 'usda-organic',
      name: 'USDA Certified Organic',
      shortName: 'Organic',
      type: 'organic',
      issuingBody: 'USDA National Organic Program',
      description: 'Products grown and processed according to federal organic guidelines',
      requirements: [
        'No synthetic fertilizers or pesticides',
        'No GMOs',
        'Soil quality standards',
        'Annual inspection',
      ],
      validityPeriod: 12,
      verificationUrl: 'https://organic.ams.usda.gov/integrity',
    },
    {
      id: 'non-gmo',
      name: 'Non-GMO Project Verified',
      shortName: 'Non-GMO',
      type: 'specialty',
      issuingBody: 'Non-GMO Project',
      description: 'Products verified to be produced according to non-GMO best practices',
      requirements: [
        'No genetically modified organisms',
        'Testing and traceability',
        'Segregation practices',
      ],
      validityPeriod: 12,
    },
    {
      id: 'gap-certified',
      name: 'Good Agricultural Practices (GAP)',
      shortName: 'GAP',
      type: 'safety',
      issuingBody: 'USDA',
      description: 'Certification for safe food handling and production practices',
      requirements: [
        'Worker health and hygiene',
        'Water quality testing',
        'Sanitation practices',
        'Traceability systems',
      ],
      validityPeriod: 12,
    },
    {
      id: 'florida-fresh',
      name: 'Fresh From Florida',
      shortName: 'FL Fresh',
      type: 'local',
      issuingBody: 'Florida Department of Agriculture',
      description: 'Products grown, raised, or made in Florida',
      requirements: [
        'Florida-based farm or producer',
        'Quality standards compliance',
        'Proper licensing',
      ],
      validityPeriod: 24,
    },
    {
      id: 'sustainable-ag',
      name: 'Sustainable Agriculture Certified',
      shortName: 'Sustainable',
      type: 'sustainability',
      issuingBody: 'Sustainable Agriculture Network',
      description: 'Farms using environmentally responsible practices',
      requirements: [
        'Soil conservation practices',
        'Water conservation',
        'Biodiversity protection',
        'Reduced carbon footprint',
      ],
      validityPeriod: 36,
    },
    {
      id: 'animal-welfare',
      name: 'Certified Humane',
      shortName: 'Humane',
      type: 'specialty',
      issuingBody: 'Humane Farm Animal Care',
      description: 'Animals raised with care according to humane standards',
      requirements: [
        'Adequate space and shelter',
        'Access to outdoors',
        'No antibiotics or hormones',
        'Humane slaughter practices',
      ],
      validityPeriod: 12,
    },
    {
      id: 'bee-friendly',
      name: 'Bee Friendly Farming',
      shortName: 'Bee Friendly',
      type: 'sustainability',
      issuingBody: 'Pollinator Partnership',
      description: 'Farms that protect and promote pollinator health',
      requirements: [
        'Pollinator habitat creation',
        'Reduced pesticide use',
        'Integrated pest management',
      ],
      validityPeriod: 12,
    },
    {
      id: 'rainforest-alliance',
      name: 'Rainforest Alliance Certified',
      shortName: 'RA Certified',
      type: 'sustainability',
      issuingBody: 'Rainforest Alliance',
      description: 'Farms meeting comprehensive sustainability standards',
      requirements: [
        'Forest and biodiversity conservation',
        'Climate action',
        'Human rights protection',
        'Improved livelihoods',
      ],
      validityPeriod: 36,
    },
  ];

  /**
   * Get all available certifications
   */
  getAllCertifications(): Certification[] {
    return this.certifications;
  }

  /**
   * Get certification by ID
   */
  getCertificationById(id: string): Certification | null {
    return this.certifications.find(c => c.id === id) || null;
  }

  /**
   * Get certifications by type
   */
  getCertificationsByType(type: Certification['type']): Certification[] {
    return this.certifications.filter(c => c.type === type);
  }

  /**
   * Verify certification status
   */
  verifyCertification(
    farmCert: FarmCertification
  ): { isValid: boolean; status: string; message: string } {
    const now = new Date();
    const expiry = new Date(farmCert.expiryDate);

    if (farmCert.status === 'suspended') {
      return {
        isValid: false,
        status: 'suspended',
        message: 'Certification has been suspended',
      };
    }

    if (now > expiry) {
      return {
        isValid: false,
        status: 'expired',
        message: 'Certification has expired',
      };
    }

    const daysUntilExpiry = Math.floor(
      (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiry <= 30) {
      return {
        isValid: true,
        status: 'expiring-soon',
        message: `Certification expires in ${daysUntilExpiry} days`,
      };
    }

    return {
      isValid: true,
      status: 'active',
      message: 'Certification is valid and active',
    };
  }

  /**
   * Calculate farm trust score based on certifications
   */
  calculateTrustScore(activeCertifications: string[]): number {
    const weights: { [key: string]: number } = {
      'usda-organic': 25,
      'gap-certified': 20,
      'florida-fresh': 15,
      'sustainable-ag': 15,
      'non-gmo': 10,
      'animal-welfare': 10,
      'bee-friendly': 5,
      'rainforest-alliance': 10,
    };

    let score = 0;
    activeCertifications.forEach(certId => {
      score += weights[certId] || 5;
    });

    return Math.min(100, score);
  }

  /**
   * Generate certification badges for display
   */
  generateBadges(certificationIds: string[]): { id: string; label: string; color: string }[] {
    const colors: { [key: string]: string } = {
      organic: '#2D5A27',
      sustainability: '#4CAF50',
      safety: '#2196F3',
      local: '#FF9800',
      specialty: '#9C27B0',
    };

    return certificationIds.map(id => {
      const cert = this.getCertificationById(id);
      if (!cert) return null;

      return {
        id: cert.id,
        label: cert.shortName,
        color: colors[cert.type] || '#666',
      };
    }).filter(Boolean) as { id: string; label: string; color: string }[];
  }

  /**
   * Get certification requirements
   */
  getCertificationRequirements(certId: string): {
    certification: Certification | null;
    steps: string[];
    estimatedTime: string;
    cost: string;
  } {
    const cert = this.getCertificationById(certId);

    if (!cert) {
      return {
        certification: null,
        steps: [],
        estimatedTime: 'Unknown',
        cost: 'Unknown',
      };
    }

    const steps = [
      'Review certification requirements',
      'Prepare documentation and records',
      'Submit application to certifying body',
      'Schedule inspection',
      'Complete on-site inspection',
      'Address any corrective actions',
      'Receive certification',
    ];

    const timeEstimates: { [key: string]: string } = {
      'usda-organic': '3-6 months (plus 3-year transition)',
      'gap-certified': '2-4 months',
      'florida-fresh': '1-2 months',
      default: '2-4 months',
    };

    const costEstimates: { [key: string]: string } = {
      'usda-organic': '$750 - $2,000 annually',
      'gap-certified': '$400 - $1,500 annually',
      'florida-fresh': '$50 - $200 annually',
      default: '$200 - $1,000 annually',
    };

    return {
      certification: cert,
      steps,
      estimatedTime: timeEstimates[certId] || timeEstimates.default,
      cost: costEstimates[certId] || costEstimates.default,
    };
  }

  /**
   * Compare farm certifications
   */
  compareFarmCertifications(
    farm1Certs: string[],
    farm2Certs: string[]
  ): {
    shared: Certification[];
    uniqueToFarm1: Certification[];
    uniqueToFarm2: Certification[];
    farm1Score: number;
    farm2Score: number;
  } {
    const shared = farm1Certs
      .filter(c => farm2Certs.includes(c))
      .map(id => this.getCertificationById(id))
      .filter(Boolean) as Certification[];

    const uniqueToFarm1 = farm1Certs
      .filter(c => !farm2Certs.includes(c))
      .map(id => this.getCertificationById(id))
      .filter(Boolean) as Certification[];

    const uniqueToFarm2 = farm2Certs
      .filter(c => !farm1Certs.includes(c))
      .map(id => this.getCertificationById(id))
      .filter(Boolean) as Certification[];

    return {
      shared,
      uniqueToFarm1,
      uniqueToFarm2,
      farm1Score: this.calculateTrustScore(farm1Certs),
      farm2Score: this.calculateTrustScore(farm2Certs),
    };
  }

  /**
   * Search certifications
   */
  searchCertifications(query: string): Certification[] {
    const normalized = query.toLowerCase();
    return this.certifications.filter(
      c =>
        c.name.toLowerCase().includes(normalized) ||
        c.shortName.toLowerCase().includes(normalized) ||
        c.description.toLowerCase().includes(normalized) ||
        c.type.includes(normalized)
    );
  }

  /**
   * Get recommended certifications for a farm type
   */
  getRecommendedCertifications(
    farmType: 'produce' | 'dairy' | 'eggs' | 'mixed'
  ): Certification[] {
    const recommendations: { [key: string]: string[] } = {
      produce: ['usda-organic', 'gap-certified', 'florida-fresh', 'bee-friendly'],
      dairy: ['usda-organic', 'animal-welfare', 'florida-fresh'],
      eggs: ['usda-organic', 'animal-welfare', 'florida-fresh', 'non-gmo'],
      mixed: ['usda-organic', 'gap-certified', 'florida-fresh', 'sustainable-ag'],
    };

    const certIds = recommendations[farmType] || recommendations.mixed;
    return certIds
      .map(id => this.getCertificationById(id))
      .filter(Boolean) as Certification[];
  }
}

const certificationVerificationAgent = new CertificationVerificationAgent();
export default certificationVerificationAgent;
